import { PageHeader } from '@/client/components/common/PageHeader';
import { RequestDialog } from '@/client/components/timeoff/RequestDialog';
import { TimeOffCalendar } from '@/client/components/timeoff/TimeOffCalendar';
import { TimeOffRightRail } from '@/client/components/timeoff/TimeOffRightRail';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarRange } from 'lucide-react';
import { Plus } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_app/timeoff')({
  component: TimeOffPage,
});

function TimeOffPage() {
  const [tab, setTab] = useState<'team' | 'me'>('team');
  const [monthOffset, setMonthOffset] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.role === 'ADMIN';

  const base = new Date();
  const d = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;

  const { data: requests = [] } = trpc.timeoff.list.useQuery({ year, month });
  const { data: balance = [] } = trpc.timeoff.myBalance.useQuery();
  const { data: pendingRequests = [] } = trpc.timeoff.listPending.useQuery(undefined, {
    enabled: isAdmin,
  });

  const myRequests = me ? requests.filter((r) => r.user_id === me.id) : [];

  return (
    <div className="flex h-full flex-col gap-6">
      <PageHeader
        title="Time off"
        eyebrow="Workspace"
        icon={CalendarRange}
        actions={
          <>
            <div className="flex overflow-hidden rounded-md border">
              {(['team', 'me'] as const).map((t, i) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={cn(
                    'px-3.5 py-1.5 text-sm font-medium transition-colors',
                    i === 0 && 'border-r',
                    tab === t
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t === 'team' ? 'Team' : 'My time off'}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Request time off
            </Button>
          </>
        }
      />

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto rounded-lg border lg:grid-cols-[1fr_360px] lg:overflow-hidden">
        <TimeOffCalendar
          requests={requests}
          monthOffset={monthOffset}
          onMonthChange={setMonthOffset}
          tab={tab}
          currentUserId={me?.id ?? ''}
        />
        <TimeOffRightRail
          isAdmin={isAdmin}
          pendingRequests={pendingRequests}
          myRequests={myRequests}
          balance={balance}
        />
      </div>

      <RequestDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
